// src/components/pipelines/PipelineSettingsModal.tsx
//
// Manage an EXISTING pipeline: rename it, edit its description, and add,
// rename, reorder, re-classify (open / won / lost) and remove its stages.
//
// Every action persists on its own, immediately, through the server's
// transactional pipeline service — there is no "save all" that could half
// apply. The list re-reads the server after each change, so what is shown is
// what is stored, in the stored order.
//
// REMOVING A STAGE never strands a deal. If the stage has deals, removal asks
// where they should go (another stage of this pipeline) and the server moves
// them and removes the stage in one transaction. The confirmation is an inline
// row, not a second modal.
//
// ARCHIVING retires the pipeline without touching its deals: they stay in
// their stages with their statuses, as a read-only record. While archived the
// dialog offers Restore (and Delete, which the server still allows only for an
// EMPTY pipeline) instead of the editors — the server refuses every
// structural edit with PIPELINE_ARCHIVED. The default pipeline cannot be
// archived; "Make default" on another pipeline is offered here so that rule
// never leaves anyone stuck.
//
// Shown only to people who can manage pipelines (`canManagePipelines` on the
// board: deals.admin AND the owner/admin floor the server applies). The
// server enforces the same gates; this component assumes, but does not grant.

import { DragDropContext, Droppable, Draggable, type DropResult, type DroppableProvided } from '@hello-pangea/dnd';
import { useEffect, useId, useState } from 'react';
import Modal from '../ui/Modal';
import ConfirmDialog from '../ui/ConfirmDialog';
import {
  useGetPipelineQuery,
  useUpdatePipelineMutation,
  useDeletePipelineMutation,
  useArchivePipelineMutation,
  useRestorePipelineMutation,
  useCreateStageMutation,
  useUpdateStageMutation,
  useDeleteStageMutation,
  useReorderStagesMutation,
} from '../../api/pipelinesApi';
import type { PipelineArchiveResponse, PipelineStage, StageOutcome } from '../../types/api';
import { OUTCOME_SHORT, isArchived, moveItem, outcomeOf, pipelineErrorMessage } from './stageOutcome';

interface Props {
  pipelineId: string;
  onClose: () => void;
  /** Called after the pipeline is deleted, so the board can select another. */
  onDeleted: () => void;
  /** Called after an archive or restore succeeds, with the server's answer. */
  onArchiveChange?: (kind: 'archived' | 'restored', result: PipelineArchiveResponse) => void;
}

type Notice = { kind: 'ok' | 'error'; text: string } | null;

export default function PipelineSettingsModal({ pipelineId, onClose, onDeleted, onArchiveChange }: Props) {
  const baseId = useId();
  const { data: pipeline, isLoading, isFetching } = useGetPipelineQuery(pipelineId);
  const [updatePipeline, { isLoading: savingDetails }] = useUpdatePipelineMutation();
  const [deletePipeline, { isLoading: deletingPipeline }] = useDeletePipelineMutation();
  const [archivePipeline] = useArchivePipelineMutation();
  const [restorePipeline, { isLoading: restoring }] = useRestorePipelineMutation();
  const [createStage, { isLoading: adding }] = useCreateStageMutation();
  const [updateStage] = useUpdateStageMutation();
  const [deleteStage, { isLoading: removing }] = useDeleteStageMutation();
  const [reorderStages, { isLoading: reordering }] = useReorderStagesMutation();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [newStage, setNewStage] = useState('');
  const [notice, setNotice] = useState<Notice>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [destination, setDestination] = useState('');
  const [needsDestination, setNeedsDestination] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (pipeline) {
      setName(pipeline.name);
      setDescription(pipeline.description ?? '');
    }
  }, [pipeline?.id, pipeline?.name, pipeline?.description]); // eslint-disable-line react-hooks/exhaustive-deps

  const stages: PipelineStage[] = [...(pipeline?.stages ?? [])].sort((a, b) => a.order - b.order);
  const busy = isFetching || reordering || removing || adding;

  async function run(action: () => Promise<unknown>, ok: string, fallback: string): Promise<boolean> {
    setNotice(null);
    try {
      await action();
      setNotice({ kind: 'ok', text: ok });
      return true;
    } catch (err) {
      setNotice({ kind: 'error', text: pipelineErrorMessage(err, fallback) });
      return false;
    }
  }

  const saveDetails = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNotice({ kind: 'error', text: 'The pipeline needs a name.' });
      return;
    }
    void run(
      () => updatePipeline({ id: pipelineId, name: name.trim(), description: description.trim() || null }).unwrap(),
      'Pipeline details saved.',
      'Could not save the pipeline details.',
    );
  };

  const move = (from: number, to: number) => {
    if (to < 0 || to >= stages.length || from === to) return;
    const ids = moveItem(stages, from, to).map((s) => s.id);
    void run(() => reorderStages({ pipelineId, stageIds: ids }).unwrap(), 'Stage order saved.', 'Could not save the new order.');
  };

  const onDragEnd = (r: DropResult) => {
    if (r.destination) move(r.source.index, r.destination.index);
  };

  const commitName = (stage: PipelineStage) => {
    const draft = drafts[stage.id];
    if (draft === undefined || draft.trim() === stage.name) return;
    void run(
      () => updateStage({ pipelineId, stageId: stage.id, name: draft }).unwrap(),
      `Renamed to “${draft.trim()}”.`,
      'Could not rename the stage.',
    ).then((ok) => {
      if (!ok) return;
      setDrafts((d) => {
        const { [stage.id]: _, ...rest } = d;
        return rest;
      });
    });
  };

  const setOutcome = (stage: PipelineStage, outcome: StageOutcome) =>
    run(
      () => updateStage({ pipelineId, stageId: stage.id, outcome }).unwrap(),
      `“${stage.name}” is now ${OUTCOME_SHORT[outcome].toLowerCase()}.`,
      'Could not change the stage.',
    );

  const addStage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newStage.trim()) return;
    void run(
      () => createStage({ pipelineId, name: newStage.trim() }).unwrap(),
      `Added “${newStage.trim()}”.`,
      'Could not add the stage.',
    ).then((ok) => ok && setNewStage(''));
  };

  const startRemove = (stage: PipelineStage) => {
    setRemovingId(stage.id);
    setDestination('');
    setNeedsDestination((stage.dealCount ?? 0) > 0);
    setNotice(null);
  };

  const confirmRemove = async (stage: PipelineStage) => {
    setNotice(null);
    try {
      await deleteStage({ pipelineId, stageId: stage.id, moveDealsToStageId: destination || undefined }).unwrap();
      setRemovingId(null);
      setNotice({ kind: 'ok', text: `Removed “${stage.name}”.` });
    } catch (err) {
      // Deals the caller cannot see still count: the server is the authority
      // on whether a destination is needed.
      if ((err as { data?: { code?: string } })?.data?.code === 'STAGE_HAS_DEALS') setNeedsDestination(true);
      setNotice({ kind: 'error', text: pipelineErrorMessage(err, 'Could not remove the stage.') });
    }
  };

  const makeDefault = () =>
    void run(
      () => updatePipeline({ id: pipelineId, isDefault: true }).unwrap(),
      'This is now the default pipeline.',
      'Could not make this the default pipeline.',
    );

  const archive = async () => {
    setArchiveError(null);
    try {
      const result = await archivePipeline(pipelineId).unwrap();
      setConfirmArchive(false);
      onArchiveChange?.('archived', result);
    } catch (err) {
      // Stays open and says why, inside the dialog the user is looking at.
      setArchiveError(pipelineErrorMessage(err, 'Could not archive the pipeline.'));
    }
  };

  const restore = async () => {
    setNotice(null);
    try {
      const result = await restorePipeline(pipelineId).unwrap();
      setNotice({ kind: 'ok', text: 'Pipeline restored.' });
      onArchiveChange?.('restored', result);
    } catch (err) {
      setNotice({ kind: 'error', text: pipelineErrorMessage(err, 'Could not restore the pipeline.') });
    }
  };

  const archived = isArchived(pipeline);

  const removePipeline = async () => {
    const ok = await run(() => deletePipeline(pipelineId).unwrap(), 'Pipeline deleted.', 'Could not delete the pipeline.');
    if (ok) onDeleted();
    else setConfirmDelete(false);
  };

  return (
    // Escape belongs to the confirmation while it is open — otherwise one key
    // press would close both dialogs.
    <Modal isOpen onClose={onClose} title="Pipeline settings" size="lg" closeOnEscape={!confirmArchive}>
      <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1" data-testid="pipeline-settings">
        {notice && (
          <p
            role={notice.kind === 'error' ? 'alert' : 'status'}
            className={`rounded-lg border px-3 py-2 text-sm ${
              notice.kind === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-green-200 bg-green-50 text-green-800'
            }`}
          >
            {notice.text}
          </p>
        )}

        {isLoading || !pipeline ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : archived ? (
          <>
            {/* ── Archived: read-only ─────────────────────────────────── */}
            <section aria-labelledby={`${baseId}-archived`} className="space-y-3" data-testid="pipeline-settings-archived">
              <h3 id={`${baseId}-archived`} className="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900">
                <span className="min-w-0 break-words">{pipeline.name}</span>
                <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">Archived</span>
              </h3>
              <p className="text-sm text-gray-600">
                Archived{pipeline.archivedAt ? ` on ${new Date(pipeline.archivedAt).toLocaleDateString()}` : ''}. Its deals
                are kept exactly as they were. While it is archived, no deals can be added or moved and its details and
                stages cannot be changed.
              </p>
              <ol className="space-y-1 text-sm text-gray-700" aria-label="Stages in order">
                {stages.map((stage) => (
                  <li key={stage.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-1.5">
                    <span className="min-w-0 break-words">{stage.name}</span>
                    <span className="text-xs text-gray-500">
                      {OUTCOME_SHORT[outcomeOf(stage)]}
                      {typeof stage.dealCount === 'number' ? ` · ${stage.dealCount} deal${stage.dealCount === 1 ? '' : 's'}` : ''}
                    </span>
                  </li>
                ))}
              </ol>
              <button
                type="button"
                onClick={() => void restore()}
                disabled={restoring}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {restoring ? 'Restoring…' : 'Restore pipeline'}
              </button>
            </section>
          </>
        ) : (
          <>
            {/* ── Details ─────────────────────────────────────────────── */}
            <form onSubmit={saveDetails} className="space-y-3" aria-labelledby={`${baseId}-details`}>
              <h3 id={`${baseId}-details`} className="text-sm font-semibold text-gray-900">
                Details
              </h3>
              <div>
                <label htmlFor={`${baseId}-name`} className="mb-1 block text-sm font-medium text-gray-700">
                  Name *
                </label>
                <input
                  id={`${baseId}-name`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={255}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label htmlFor={`${baseId}-desc`} className="mb-1 block text-sm font-medium text-gray-700">
                  Description
                </label>
                <textarea
                  id={`${baseId}-desc`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  disabled={savingDetails || (name.trim() === pipeline.name && (description.trim() || '') === (pipeline.description ?? ''))}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {savingDetails ? 'Saving…' : 'Save details'}
                </button>
                {pipeline.isDefault ? (
                  <span className="text-xs text-gray-500">This is the default pipeline.</span>
                ) : (
                  <button
                    type="button"
                    onClick={makeDefault}
                    disabled={savingDetails}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Make default
                  </button>
                )}
              </div>
            </form>

            {/* ── Stages ──────────────────────────────────────────────── */}
            <section aria-labelledby={`${baseId}-stages`} className="space-y-2">
              <h3 id={`${baseId}-stages`} className="text-sm font-semibold text-gray-900">
                Stages
              </h3>
              <p className="text-xs text-gray-500">
                Changes save immediately. Drag a stage or use the arrows to reorder. A stage marked <strong>Won</strong>{' '}
                or <strong>Lost</strong> closes the deals in it.
              </p>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId={`${baseId}-list`}>
                  {(provided: DroppableProvided) => (
                    <ol ref={provided.innerRef} {...provided.droppableProps} className="space-y-1.5" aria-label="Stages in order">
                      {stages.map((stage, index) => {
                        const outcome = outcomeOf(stage);
                        const nameValue = drafts[stage.id] ?? stage.name;
                        const others = stages.filter((s) => s.id !== stage.id);
                        return (
                          <Draggable key={stage.id} draggableId={stage.id} index={index} isDragDisabled={busy}>
                            {(drag) => (
                              <li ref={drag.innerRef} {...drag.draggableProps} className="rounded-lg border border-gray-200 bg-white">
                                <div className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                                  <span
                                    {...drag.dragHandleProps}
                                    aria-label={`Drag ${stage.name} to reorder`}
                                    className="cursor-grab select-none px-1 text-gray-400"
                                  >
                                    ⋮⋮
                                  </span>
                                  <label className="sr-only" htmlFor={`${baseId}-sn-${stage.id}`}>
                                    Name of stage {index + 1}
                                  </label>
                                  <input
                                    id={`${baseId}-sn-${stage.id}`}
                                    value={nameValue}
                                    maxLength={100}
                                    onChange={(e) => setDrafts((d) => ({ ...d, [stage.id]: e.target.value }))}
                                    onBlur={() => commitName(stage)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        commitName(stage);
                                      }
                                    }}
                                    className="min-w-0 flex-1 basis-32 rounded-md border border-gray-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                                  />
                                  <label className="sr-only" htmlFor={`${baseId}-so-${stage.id}`}>
                                    Outcome of {stage.name}
                                  </label>
                                  <select
                                    id={`${baseId}-so-${stage.id}`}
                                    value={outcome}
                                    disabled={busy}
                                    onChange={(e) => void setOutcome(stage, e.target.value as StageOutcome)}
                                    className="w-24 rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                                  >
                                    {(['open', 'won', 'lost'] as const).map((o) => (
                                      <option key={o} value={o}>
                                        {OUTCOME_SHORT[o]}
                                      </option>
                                    ))}
                                  </select>
                                  {typeof stage.dealCount === 'number' && (
                                    <span className="w-16 text-right text-xs text-gray-500">
                                      {stage.dealCount} deal{stage.dealCount === 1 ? '' : 's'}
                                    </span>
                                  )}
                                  <div className="flex items-center">
                                    <button
                                      type="button"
                                      onClick={() => move(index, index - 1)}
                                      disabled={busy || index === 0}
                                      aria-label={`Move ${stage.name} up`}
                                      className="h-8 w-8 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                                    >
                                      ↑
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => move(index, index + 1)}
                                      disabled={busy || index === stages.length - 1}
                                      aria-label={`Move ${stage.name} down`}
                                      className="h-8 w-8 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30"
                                    >
                                      ↓
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => startRemove(stage)}
                                      disabled={busy}
                                      aria-label={`Remove ${stage.name}`}
                                      aria-expanded={removingId === stage.id}
                                      className="h-8 w-8 rounded text-gray-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-30"
                                    >
                                      ×
                                    </button>
                                  </div>
                                </div>

                                {removingId === stage.id && (
                                  <div
                                    role="group"
                                    aria-label={`Remove ${stage.name}`}
                                    className="space-y-2 border-t border-red-100 bg-red-50 px-3 py-2"
                                  >
                                    {needsDestination ? (
                                      <>
                                        <p className="text-sm text-red-900">
                                          “{stage.name}” has deals. Choose where they move — they are not deleted.
                                        </p>
                                        <label className="block text-xs font-medium text-red-900" htmlFor={`${baseId}-dest-${stage.id}`}>
                                          Move deals to
                                        </label>
                                        <select
                                          id={`${baseId}-dest-${stage.id}`}
                                          value={destination}
                                          onChange={(e) => setDestination(e.target.value)}
                                          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm sm:w-64"
                                        >
                                          <option value="">Choose a stage…</option>
                                          {others.map((o) => (
                                            <option key={o.id} value={o.id}>
                                              {o.name}
                                              {outcomeOf(o) !== 'open' ? ` (${OUTCOME_SHORT[outcomeOf(o)]})` : ''}
                                            </option>
                                          ))}
                                        </select>
                                      </>
                                    ) : (
                                      <p className="text-sm text-red-900">Remove the empty stage “{stage.name}”?</p>
                                    )}
                                    <div className="flex gap-2">
                                      <button
                                        type="button"
                                        onClick={() => void confirmRemove(stage)}
                                        disabled={removing || (needsDestination && !destination)}
                                        className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                      >
                                        {removing ? 'Removing…' : needsDestination ? 'Move deals and remove' : 'Remove stage'}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setRemovingId(null)}
                                        className="rounded-md px-3 py-1.5 text-sm text-gray-700 hover:bg-white"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </li>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </ol>
                  )}
                </Droppable>
              </DragDropContext>

              <form onSubmit={addStage} className="flex gap-2">
                <label htmlFor={`${baseId}-new`} className="sr-only">
                  New stage name
                </label>
                <input
                  id={`${baseId}-new`}
                  value={newStage}
                  onChange={(e) => setNewStage(e.target.value)}
                  placeholder="New stage name"
                  maxLength={100}
                  className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={adding || !newStage.trim()}
                  className="shrink-0 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {adding ? 'Adding…' : 'Add stage'}
                </button>
              </form>
            </section>

            {/* ── Archive ─────────────────────────────────────────────── */}
            <section aria-labelledby={`${baseId}-archive`} className="space-y-2 border-t border-gray-200 pt-4">
              <h3 id={`${baseId}-archive`} className="text-sm font-semibold text-gray-900">
                Archive pipeline
              </h3>
              <p id={`${baseId}-archive-help`} className="text-xs text-gray-500">
                {pipeline.isDefault
                  ? 'This is the default pipeline. Make another pipeline the default before archiving this one.'
                  : "Retire this pipeline without deleting anything. Its deals stay exactly as they are; new deals can't be added and deals can't move between stages until you restore it, which you can do at any time."}
              </p>
              <button
                type="button"
                onClick={() => {
                  setArchiveError(null);
                  setConfirmArchive(true);
                }}
                disabled={pipeline.isDefault}
                aria-describedby={`${baseId}-archive-help`}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Archive pipeline…
              </button>
            </section>
          </>
        )}

        {pipeline && !isLoading && (
          <>
            {/* ── Delete ──────────────────────────────────────────────── */}
            <section aria-labelledby={`${baseId}-danger`} className="space-y-2 border-t border-gray-200 pt-4">
              <h3 id={`${baseId}-danger`} className="text-sm font-semibold text-gray-900">
                Delete pipeline
              </h3>
              <p className="text-xs text-gray-500">
                Only an empty pipeline can be deleted. Move or delete its deals first — deleting a pipeline never deletes deals.
              </p>
              {confirmDelete ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-red-900">Delete “{pipeline.name}” and its stages?</span>
                  <button
                    type="button"
                    onClick={() => void removePipeline()}
                    disabled={deletingPipeline}
                    className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                  >
                    {deletingPipeline ? 'Deleting…' : 'Delete pipeline'}
                  </button>
                  <button type="button" onClick={() => setConfirmDelete(false)} className="px-3 py-1.5 text-sm text-gray-700">
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmDelete(true)}
                  className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
                >
                  Delete pipeline…
                </button>
              )}
            </section>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmArchive}
        title={`Archive “${pipeline?.name ?? 'this pipeline'}”?`}
        message="Its deals are kept exactly as they are — nothing is deleted, moved or closed. While it is archived, no new deals can be added to it, its deals cannot be moved between stages, and its stages cannot be edited. Automations that use it will fail their deal steps until it is restored. You can restore it at any time."
        confirmLabel="Archive pipeline"
        variant="warning"
        error={archiveError}
        onConfirm={archive}
        onCancel={() => {
          setConfirmArchive(false);
          setArchiveError(null);
        }}
      />
    </Modal>
  );
}
